import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Navbar as BootstrapNavbar,
  Nav,
  Container,
  NavDropdown,
} from "react-bootstrap";
import AuthContext from "../contexts/AuthContext";
import api, { publicApi } from "../api";
import CustomButton from "./CustomButton";
import CustomBadge from "./CustomBadge";
import colorPalette from "../styles/colorPalette";
import SubscriptionStatus from "./SubscriptionStatus";

const Navbar = () => {
  const { auth, logout } = useContext(AuthContext);
  const { isAuthenticated, user, isAdmin } = auth;
  const [systemSettings, setSystemSettings] = useState({
    enable_registration: "true",
    enable_google_login: "true",
  });

  // Fetch system settings
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        console.log("Fetching system settings from API...");
        const response = await publicApi.get("/api/public-settings/");
        console.log("System settings response:", response.data);
        if (response && response.data) {
          setSystemSettings(response.data);
        } else {
          // If we get an empty response, keep using defaults
          console.log("Empty response, using default system settings");
        }
      } catch (err) {
        console.error("Error fetching system settings:", err);
        // Continue using default values from initial state
        console.log("Using default system settings due to API error");
      }
    };

    fetchSystemSettings();
  }, []);

  // Use auth context to determine if user has admin access
  const hasAdminAccess =
    isAuthenticated && (user?.is_staff || user?.is_superuser || isAdmin);

  // Function to handle logout
  const handleLogout = () => {
    logout();
  };

  // Style for admin badge
  const adminBadgeStyles = {
    background: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
    color: colorPalette.white,
    fontSize: "0.7rem",
    fontWeight: "600",
    padding: "2px 6px",
    borderRadius: "10px",
    border: "none",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginLeft: "6px",
  };

  // Professional Navbar Styles
  const navbarStyles = {
    background: `linear-gradient(135deg, ${colorPalette.white} 0%, ${colorPalette.lightGrey} 100%)`,
    borderBottom: `1px solid ${colorPalette.grey}`,
    boxShadow: "0 2px 20px rgba(0, 0, 0, 0.08)",
    padding: "0.75rem 0",
    backdropFilter: "blur(10px)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  };

  const containerStyles = {
    maxWidth: "1200px",
    margin: "0 auto",
  };

  const brandStyles = {
    fontWeight: "700",
    fontSize: "1.5rem",
    color: colorPalette.primary,
    textDecoration: "none",
    letterSpacing: "-0.5px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const navLinkStyles = {
    color: colorPalette.darkGrey,
    fontWeight: "500",
    fontSize: "0.95rem",
    padding: "0.5rem 1rem",
    margin: "0 0.2rem",
    borderRadius: "6px",
    textDecoration: "none",
    transition: "all 0.3s ease",
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const navDropdownStyles = {
    ...navLinkStyles,
    padding: "0.5rem 0",
  };

  const dropdownItemStyles = {
    color: colorPalette.darkGrey,
    fontWeight: "500",
    fontSize: "0.95rem",
    padding: "0.5rem 1rem",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const userInfoStyles = {
    color: colorPalette.darkGrey,
    fontWeight: "500",
    fontSize: "0.9rem",
    padding: "0.5rem 1rem",
    borderRadius: "6px",
    background: colorPalette.lightGrey,
    border: `1px solid ${colorPalette.grey}`,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const logoutButtonStyles = {
    background: "transparent",
    border: `1px solid ${colorPalette.primary}`,
    color: colorPalette.primary,
    fontWeight: "500",
    borderRadius: "6px",
    padding: "0.5rem 1rem",
    fontSize: "0.9rem",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    textDecoration: "none",
  };

  const authLinkStyles = {
    background: "transparent",
    border: `1px solid ${colorPalette.primary}`,
    color: colorPalette.primary,
    fontWeight: "500",
    borderRadius: "6px",
    padding: "0.5rem 1rem",
    fontSize: "0.9rem",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    textDecoration: "none",
  };

  // Add CSS for navbar hover effects
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .nav-link-hover:hover {
        background-color: rgba(0, 0, 0, 0.05);
        transform: translateY(-1px);
      }
      .logout-hover:hover {
        background-color: ${colorPalette.primary};
        color: white;
      }
      .auth-link-hover:hover {
        background-color: ${colorPalette.primary};
        color: white;
      }
      .dropdown-item.active, .dropdown-item:active {
        background-color: ${colorPalette.primary};
        color: white;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      <BootstrapNavbar expand="lg" style={navbarStyles}>
        <Container style={containerStyles}>
          <BootstrapNavbar.Brand as={Link} to="/" style={brandStyles}>
            Lion City Exams
          </BootstrapNavbar.Brand>
          <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
          <BootstrapNavbar.Collapse id="basic-navbar-nav">
            {isAuthenticated && (
              <Nav className="me-auto">
                <Nav.Link
                  as={Link}
                  to="/data"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  🏠 Home
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/worksheets"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  📑 Worksheets
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/my-worksheets"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  📊 My Worksheets
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/shared-worksheets"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  🔄 Shared Worksheets
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/my-questions"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  ❓ My Questions
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/shared-questions"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  💬 Shared Questions
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/payment-methods"
                  style={navLinkStyles}
                  className="nav-link-hover"
                >
                  💳 Payment Methods
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/upload-question"
                  style={{
                    ...navLinkStyles,
                    display: "none", // Hidden as requested
                  }}
                >
                  Upload Question
                </Nav.Link>

                {/* Admin Section - Now as a dropdown */}
                {hasAdminAccess && (
                  <>
                    <div
                      style={{
                        borderLeft: `2px solid ${colorPalette.grey}`,
                        margin: "0 1rem",
                        height: "30px",
                        alignSelf: "center",
                      }}
                    />

                    <NavDropdown
                      title={
                        <span>
                          👑 Admin
                          <span style={adminBadgeStyles}>ADMIN</span>
                        </span>
                      }
                      id="admin-dropdown"
                      style={navDropdownStyles}
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/admin-data"
                        style={dropdownItemStyles}
                      >
                        👑 Admin Dashboard
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/upload"
                        style={dropdownItemStyles}
                      >
                        📤 Upload CSV
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/pending-questions"
                        style={dropdownItemStyles}
                      >
                        ⏳ Pending Questions
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/user-management"
                        style={dropdownItemStyles}
                      >
                        👥 User Management
                      </NavDropdown.Item>

                      <NavDropdown.Divider />

                      <NavDropdown.Item
                        as={Link}
                        to="/subscription-metrics"
                        style={dropdownItemStyles}
                      >
                        📊 Subscription Metrics
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/subscription-audit-logs"
                        style={dropdownItemStyles}
                      >
                        🔍 Audit Logs
                      </NavDropdown.Item>
                    </NavDropdown>
                  </>
                )}
              </Nav>
            )}

            <Nav className="ms-auto">
              {isAuthenticated ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <div style={userInfoStyles}>
                    {hasAdminAccess ? "👑" : "👤"}
                    {hasAdminAccess ? "Admin: " : "Welcome, "}
                    <strong>{user?.username || "User"}</strong>
                  </div>

                  <SubscriptionStatus
                    compact={true}
                    showActions={false}
                    className="me-2"
                  />

                  <button
                    style={logoutButtonStyles}
                    className="logout-hover"
                    onClick={logout}
                  >
                    🚪 Logout
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Nav.Link
                    as={Link}
                    to="/login"
                    style={authLinkStyles}
                    className="auth-link-hover"
                  >
                    🔑 Login
                  </Nav.Link>
                  {systemSettings.enable_registration === "true" && (
                    <Nav.Link
                      as={Link}
                      to="/register"
                      style={authLinkStyles}
                      className="auth-link-hover"
                    >
                      ✨ Register
                    </Nav.Link>
                  )}
                </div>
              )}
            </Nav>
          </BootstrapNavbar.Collapse>
        </Container>
      </BootstrapNavbar>
    </>
  );
};

export default Navbar;
